import {
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  ModelUpdatedEvent,
  RowClickedEvent,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  checkboxNullableCellRenderer,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosClientSideRowConfig,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
} from '@helios/ag-grid-config'
import { SavedView } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { FileManagerNodeGridKey } from '../../../panel-key'
import { Node, UINode } from '../../models/nodes.model'

@Component({
  selector: 'hs-nodes-explorer[uiNodes]',
  templateUrl: './nodes-explorer.component.html',
})
export class NodesExplorerComponent implements OnDestroy, OnInit {
  _filterState: { [columnName: string]: unknown } | undefined
  @Input() uiNodes: UINode[] = []
  @Input() set savedViews(savedViewsStates: SavedView[] | null | undefined) {
    this._filterState = savedViewsStates?.find(
      view => view.gridKey === FileManagerNodeGridKey,
    )?.savedView.filterState
  }

  @Output() selectedExecutionChange = new EventEmitter<number | null>()
  @Output() activateEvent = new EventEmitter<Node>()
  @Output() inactivateEvent = new EventEmitter<Node>()

  private readonly destroySubject$ = new Subject<void>()
  clearFiltersOnAccountLoadToggle = new FormControl(true)

  partitionType = 'ACCOUNT'
  /** @internal */
  gridApi?: AgGridApi

  gridOptions: GridOptions = this.createGridOptions()

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  ngOnInit(): void {
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  createGridOptions(): GridOptions {
    return {
      ...heliosClientSideRowConfig,
      columnDefs: [
        {
          headerName: 'Active',
          field: 'node.execution.active',
          width: heliosDefaultColWidth.SmallIcon,
          cellRenderer: checkboxNullableCellRenderer,
        },
        {
          headerName: 'Identifier',
          field: 'node.identifier',
          type: NfColumnTypes.Identifier,
          width: heliosDefaultColWidth.Id,
        },
        {
          headerName: 'Group',
          field: 'node.execution.group',
          type: NfColumnTypes.Identifier,
          width: heliosDefaultColWidth.Id,
        },
        {
          headerName: 'Execution ID',
          field: 'node.execution.executionId',
          type: NfColumnTypes.Identifier,
          width: heliosDefaultColWidth.Id,
        },
      ],
      getContextMenuItems: params => this.getContextMenuItems(params),
      defaultColDef: {
        flex: 1,
      },
      autoGroupColumnDef: {
        headerName: 'Executions',
        minWidth: 300,
        cellRendererParams: {
          suppressCount: true,
        },
      },
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
      ]),
      treeData: true, // enable Tree Data mode
      animateRows: false,
      groupDefaultExpanded: -1, // expand all groups by default
      getDataPath: data => data.path,
      rowSelection: 'single',
      groupSelectsChildren: false, // doesn't work with treeData
      onRowClicked: event => this.onRowClicked(event),
      onGridReady: event => this.onGridReady(event),
      onModelUpdated: event =>
        event.newData && this._getAndApplyColumnFilter(event),
      onFirstDataRendered: event => this._getAndApplyColumnFilter(event),
    }
  }

  private _getAndApplyColumnFilter(
    event: ModelUpdatedEvent | FirstDataRenderedEvent,
  ): void {
    if (!event.api.isColumnFilterPresent()) {
      if (this._filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(this._filterState)
      }
    }
  }

  private async onGridReady(event: GridReadyEvent) {
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.gridApi = event
    enableHeliosCopyBehavior(event.api)

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace 
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      FileManagerNodeGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
  }

  private getContextMenuItems(
    params: GetContextMenuItemsParams,
  ): MenuItemDef[] {
    const clickedNode = params.node?.data as UINode | null
    const menuItemDefs: MenuItemDef[] = [
      {
        name: 'Activate',
        disabled:
          clickedNode == null ||
          clickedNode.node.execution == undefined ||
          clickedNode.node.execution.active,
        action: () => {
          if (clickedNode == null) {
            return
          }
          this.activateEvent.emit(clickedNode.node)
        },
      },
      {
        name: 'Inactivate',
        disabled:
          clickedNode == null ||
          clickedNode.node.execution == undefined ||
          !clickedNode.node.execution.active,
        action: () => {
          if (clickedNode == null) {
            return
          }
          this.inactivateEvent.emit(clickedNode.node)
        },
      },
    ]

    return menuItemDefs
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }

  private onRowClicked(event: RowClickedEvent) {
    const uiNode = event.data as UINode
    if (uiNode == null || uiNode.node.execution == null) {
      this.selectedExecutionChange.emit(null)
      return
    }

    this.selectedExecutionChange.emit(uiNode.node.execution.executionId)
  }
}
